/**
 * /portal/booking - Pro-only (PR-47, PR-48; ARCH-011). 404 for Studio per
 * the tier gate inherited from Phase G T-036 / T-078.
 *
 * Renders two inline Cal.com embeds (T-077): the monthly strategic call
 * (`pro-monthly-call`, 30 min) and the bookable hour (`pro-bookable-hour`,
 * 60 min). Per-month limits live in the Cal.com dashboard, not in code; see
 * `app/docs/calcom-setup.md`. The embed is the only Cal.com surface the
 * portal exposes; portal-side booking history / "upcoming calls" views are
 * deferred to a post-V1 follow-up per the 2026-05-27 ARCH-011 amendment.
 *
 * Per ARCH-011 §3, `@calcom/embed-react` is imported ONLY in this route. The
 * planned archgate rule `ARCH-011/embed-only-on-booking-route` will enforce
 * this once it arms.
 */

import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import Cal, { getCalApi } from '@calcom/embed-react';
import { getCurrentSession } from '../../lib/auth';
import {
  calLinkForEventType,
  resolveBookingCssVars,
  type CalcomEventTypeName,
} from '../../lib/booking';

export const Route = createFileRoute('/portal/booking')({
  beforeLoad: ({ context }) => {
    const portal = (context as { portal?: { isOperator: boolean; tier: string | null } }).portal;
    const allowed = portal?.isOperator || portal?.tier === 'pro';
    if (!allowed) throw notFound();
  },
  component: BookingPage,
});

interface PrefillIdentity {
  email: string;
  name: string;
}

function BookingPage() {
  const [identity, setIdentity] = useState<PrefillIdentity | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const session = await getCurrentSession();
      if (cancelled) return;
      const user = session?.user;
      const metadata = (user?.user_metadata ?? {}) as Record<string, unknown>;
      const nameCandidate =
        (typeof metadata.full_name === 'string' && metadata.full_name) ||
        (typeof metadata.name === 'string' && metadata.name) ||
        '';
      setIdentity({
        email: user?.email ?? '',
        name: nameCandidate,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-3xl space-y-10">
      <header className="space-y-3">
        <h1 className="font-display text-2xl text-[color:var(--color-navy)]">
          Booking
        </h1>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          Pro includes synchronous time. Book a strategic call or a working
          hour below. You can book one of each per calendar month. The
          confirmation and calendar invite come straight from Cal.com once you
          pick a time.
        </p>
      </header>

      <BookingEmbedSection
        title="Monthly strategic call"
        description="30 minutes. Use it for the big-picture call: positioning, sequencing, what to do next."
        eventType="pro-monthly-call"
        namespace="pro-monthly-call"
        identity={identity}
      />

      <BookingEmbedSection
        title="Bookable hour"
        description="60 minutes. Use it for a working session on a live task or deliverable."
        eventType="pro-bookable-hour"
        namespace="pro-bookable-hour"
        identity={identity}
      />
    </div>
  );
}

interface BookingEmbedSectionProps {
  title: string;
  description: string;
  eventType: CalcomEventTypeName;
  /** Cal.com embed namespace - lets `getCalApi` scope theming per instance. */
  namespace: string;
  identity: PrefillIdentity | null;
}

function BookingEmbedSection({
  title,
  description,
  eventType,
  namespace,
  identity,
}: BookingEmbedSectionProps) {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cal = await getCalApi({ namespace });
      if (cancelled) return;
      const cssVars = resolveBookingCssVars();
      cal('ui', {
        // The portal has no dark mode in V1. We bind both keys to the same
        // resolved tokens so the embed renders identically regardless of the
        // user's system preference (config.theme below pins the embed to light).
        cssVarsPerTheme: { light: cssVars, dark: cssVars },
        hideEventTypeDetails: false,
        layout: 'month_view',
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [namespace]);

  const calLink = calLinkForEventType(eventType);

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="font-display text-xl text-[color:var(--color-navy)]">
          {title}
        </h2>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          {description}
        </p>
      </div>
      <div
        className="overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--color-rule)] bg-[color:var(--color-white)]"
        style={{ minHeight: '640px' }}
      >
        <Cal
          namespace={namespace}
          calLink={calLink}
          style={{ width: '100%', height: '640px', overflow: 'scroll' }}
          config={{
            name: identity?.name ?? '',
            email: identity?.email ?? '',
            layout: 'month_view',
            theme: 'light',
          }}
        />
      </div>
    </section>
  );
}
